/**
 * Read-only classification facts shared by the review row and the opened prompt:
 * `LevelLine` (UI-SPEC §2.7, referral) and `MetaLine` (criteria, UI-SPEC §2.8; the
 * theme / use case line is an owner addition, 2026-10-20).
 */
import { Box } from '@mui/material';
import type { BrandQueryAxis, ComplementKind, TargetZone } from '@traaker/shared';
import { levelFromComplements } from '@traaker/shared';

const LEVEL_WORD: Record<string, string> = {
  // i18n: promptStudio.level.*
  L1: 'Très précis',
  L2: 'Précis',
  L3: 'Cas d’usage',
  L4: 'Catégorie',
  L5: 'Générique',
};

const COMPLEMENT_WORD: Record<ComplementKind, string> = {
  temps: 'temps', lieu: 'lieu', maniere: 'manière', moyen: 'moyen', cause: 'cause', consequence: 'conséquence', but: 'but',
};

const line = { fontSize: 11.5, color: 'text.secondary', cursor: 'default', mb: 0.5 } as const;

function joinFr(xs: string[]): string {
  return xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`;
}

export function LevelLine({
  level,
  complements,
  zone,
}: {
  level?: string | null;
  complements?: ComplementKind[];
  zone?: TargetZone | null;
}) {
  const computed = complements ? levelFromComplements(complements, zone ?? null) : null;
  const lvl = computed?.level ?? level;
  if (!lvl || !LEVEL_WORD[lvl]) return null;
  const lit = 6 - Number(lvl.slice(1));
  const reason = computed
    ? computed.effective.length === 0
      ? 'aucun complément' // i18n: promptStudio.level.none
      : `${computed.effective.length} complément${computed.effective.length > 1 ? 's' : ''} : ${joinFr(computed.effective.map((k) => COMPLEMENT_WORD[k]))}`
    : null;
  return (
    <Box
      sx={{ ...line, display: 'flex', alignItems: 'center', gap: 0.75 }}
      title="Compléments cherchés : temps, lieu, manière, moyen, cause, conséquence, but" // i18n: promptStudio.level.title
    >
      <Box sx={{ display: 'flex', gap: '2px' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ width: 7, height: 3, borderRadius: '2px', bgcolor: i < lit ? '#be185d' : '#e4e4ea' }} />
        ))}
      </Box>
      <Box component="span" sx={{ fontWeight: 700 }}>{LEVEL_WORD[lvl]}</Box>
      {reason && <Box component="span">{reason}</Box>}
    </Box>
  );
}

const TARGET_WORD: Record<string, string> = {
  // i18n: promptStudio.target.category.*
  marque: 'marque', etablissement: 'établissement', personne: 'personne', produit: 'produit',
};

export function MetaLine({
  axis,
  criteria,
  topic,
  target,
  title,
  targetOnly,
}: {
  axis?: BrandQueryAxis | 'pending';
  criteria?: string[];
  topic?: string;
  /** Image / Comparaison: the target alone (the criteria are edited as chips beside it). */
  targetOnly?: boolean;
  /** Image / Comparaison only (UI-SPEC §2.8): `undefined` = no target fact, `null` = « cible non détectée ». */
  target?: { label: string; category: string } | null;
  title?: string;
}) {
  if ((axis === 'narrative' || axis === 'comparison') && targetOnly) {
    if (target === undefined) return null;
    return (
      <Box sx={line} title={title}>
        {target
          ? `${target.label}, ${TARGET_WORD[target.category] ?? target.category}`
          : 'cible non détectée' /* i18n: promptStudio.meta.noTarget */}
      </Box>
    );
  }
  if (axis === 'narrative' || axis === 'comparison') {
    return (
      <Box sx={line} title={title}>
        {criteria?.length ? criteria.join(', ') : 'aucun critère' /* i18n: promptStudio.meta.noCriteria */}
        {target !== undefined && (
          <>
            {' · '}
            {target
              ? `${target.label}, ${TARGET_WORD[target.category] ?? target.category}`
              : 'cible non détectée' /* i18n: promptStudio.meta.noTarget */}
          </>
        )}
      </Box>
    );
  }
  if ((axis === 'authority' || axis === 'referral') && topic) {
    return (
      <Box sx={line} title={title}>
        {axis === 'authority' ? 'Thème' : 'Cas d’usage' /* i18n: promptStudio.meta.topic.* */} : {topic}
      </Box>
    );
  }
  return null;
}
